import { useState, useCallback } from 'react';

export const useWindowManager = () => {
  const [openWindows, setOpenWindows] = useState([]);
  const [minimizedWindows, setMinimizedWindows] = useState([]);
  const [focusedWindow, setFocusedWindow] = useState(null);
  const [draggedWindow, setDraggedWindow] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const openWindow = useCallback((windowId) => {
    setOpenWindows(prev => {
      if (!prev.includes(windowId)) {
        return [...prev, windowId];
      }
      return prev;
    });
    // Remove from minimized if it was minimized
    setMinimizedWindows(prev => prev.filter(id => id !== windowId));
    setFocusedWindow(windowId);
  }, []);

  const closeWindow = useCallback((windowId) => {
    setOpenWindows(prev => prev.filter(id => id !== windowId));
    setMinimizedWindows(prev => prev.filter(id => id !== windowId));
    if (focusedWindow === windowId) {
      setFocusedWindow(null);
    }
  }, [focusedWindow]);

  const minimizeWindow = useCallback((windowId) => {
    // Add to minimized windows
    setMinimizedWindows(prev => {
      if (!prev.includes(windowId)) {
        return [...prev, windowId];
      }
      return prev;
    });
    // Remove from open windows
    setOpenWindows(prev => prev.filter(id => id !== windowId));
    if (focusedWindow === windowId) {
      setFocusedWindow(null);
    }
  }, [focusedWindow]);

  const restoreWindow = useCallback((windowId) => {
    // Remove from minimized
    setMinimizedWindows(prev => prev.filter(id => id !== windowId));
    // Add back to open windows
    setOpenWindows(prev => {
      if (!prev.includes(windowId)) {
        return [...prev, windowId];
      }
      return prev;
    });
    setFocusedWindow(windowId);
  }, []);

  const maximizeWindow = useCallback((windowId) => {
    // Maximize logic is handled in the Window component
  }, []);

  const focusWindow = useCallback((windowId) => {
    setFocusedWindow(windowId);
    // If window is minimized, restore it
    if (minimizedWindows.includes(windowId)) {
      restoreWindow(windowId);
    }
  }, [minimizedWindows, restoreWindow]);

  const startDrag = useCallback((e, windowElement) => {
    if (window.innerWidth <= 768) return;
    
    e.preventDefault();
    setDraggedWindow(windowElement);
    
    if (windowElement) {
      const rect = windowElement.getBoundingClientRect();
      const clientX = e.clientX || (e.touches && e.touches[0].clientX);
      const clientY = e.clientY || (e.touches && e.touches[0].clientY);
      
      setDragOffset({
        x: clientX - rect.left,
        y: clientY - rect.top
      });
      
      windowElement.style.zIndex = Math.max(
        ...Array.from(document.querySelectorAll('.window')).map(w => 
          parseInt(w.style.zIndex) || 100
        )
      ) + 1;
    }
  }, []);

  const drag = useCallback((e) => {
    if (!draggedWindow || window.innerWidth <= 768) return;
    
    e.preventDefault();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    const newX = clientX - dragOffset.x;
    const newY = clientY - dragOffset.y;
    
    // Keep window within bounds
    const maxX = window.innerWidth - draggedWindow.offsetWidth;
    const maxY = window.innerHeight - draggedWindow.offsetHeight - 60; // Account for taskbar
    
    draggedWindow.style.left = Math.max(0, Math.min(newX, maxX)) + 'px';
    draggedWindow.style.top = Math.max(0, Math.min(newY, maxY)) + 'px';
  }, [draggedWindow, dragOffset]);

  const endDrag = useCallback(() => {
    setDraggedWindow(null);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  return {
    openWindows,
    minimizedWindows,
    focusedWindow,
    openWindow,
    closeWindow,
    minimizeWindow,
    restoreWindow,
    maximizeWindow,
    focusWindow,
    draggedWindow,
    dragOffset,
    startDrag,
    drag,
    endDrag
  };
};
